/**
 * UsersDirectory
 *
 * Shared users-management surface — the filter row + paged table +
 * bulk-action toolbar + row-action menu + all related dialogs (invite,
 * delete/enable/disable confirm, reset password, assign content).
 *
 * Two consumers today:
 *   1. The standalone Users page (`pages/Students.tsx`) — multi-workspace
 *      view; the workspace column and workspace filter both render.
 *   2. The workspace edit page's Users tab — drilled-in view for a
 *      single tenant. Pass `workspaceIdLock` to lock the directory to
 *      that workspace; the workspace column and workspace dropdown
 *      both disappear (they'd be noise — every row would have the
 *      same value).
 *
 * Everything else (audience toggle, role select, status switch, bulk
 * actions, sorting, the kebab menu) stays identical so the two
 * surfaces feel like the same tool reached two different ways.
 *
 * Page chrome (PageHeader, outer padding, layout-config side effects)
 * lives in the parent so this can sit naturally inside either a full
 * page or a tabbed editor.
 */

import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Login, UserRole, Workspace } from '../../apis/api-client';
import { usersClient, workspacesClient } from '../../apis/client-instances';
import PagedTable, { PagedTableColumn, SortDirection } from '../common/PagedTable';
import WorkspaceAutocomplete from '../common/WorkspaceAutocomplete';
import ConfirmDialog from '../ConfirmDialog';
import {
  AddIcon,
  BookmarkIcon,
  DeleteIcon,
  EditIcon,
  EmailIcon,
  LockResetIcon,
  MoreVertIcon,
  PersonAddIcon,
  PersonOffIcon,
  SearchIcon,
} from '../icons';
import InviteStudentDialog from '../students/InviteStudentDialog';
import ResetPasswordDialog from '../students/ResetPasswordDialog';
import AssignContentDialog from './AssignContentDialog';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SelectField,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '../ui';
import { alert } from '../../hooks/function';
import { usePagedList } from '../../hooks/usePagedList';
import { RootState } from '../../types';
import {
  formatDateTime,
  getRoleLabel,
  getUserDisplayName,
  getUserStatus,
  isAccountOrSystemAdmin,
} from '../../utils/roleHelpers';

// "showInactive" decides whether to also surface non-active users
// (disabled accounts, pending invites, expired invites). Off by default
// because admins almost always want the working roster — flipping the
// toggle in the filter strip relaxes the filter to show everyone.
type StatusFilter = 'active' | 'all';

// High-level "who am I looking at" partition. The page-level segmented
// toggle drives this; the role SelectField acts as a sub-filter within
// the selected partition (e.g. drill from Managers → just Workspace
// Admins). Backend takes a single `role` value today — when the API
// gains a `roles?: number[]` parameter, the Managers mode can drop the
// sub-select entirely and fetch all three admin types in one call.
type AudienceFilter = 'all' | 'learners' | 'managers';

// Role options exposed by the dropdown for each audience. Keeping this
// outside the component avoids re-creating arrays on every render.
const MANAGER_ROLE_OPTIONS = [
  { label: 'Workspace Admin', value: UserRole.WorkspaceAdmin },
  { label: 'Account Admin', value: UserRole.AccountAdmin },
  { label: 'Super Admin', value: UserRole.Admin },
];
const ALL_ROLE_OPTIONS = [
  { label: 'Learner', value: UserRole.User },
  { label: 'Workspace Admin', value: UserRole.WorkspaceAdmin },
  { label: 'Account Admin', value: UserRole.AccountAdmin },
  { label: 'Super Admin', value: UserRole.Admin },
];

export interface UsersDirectoryProps {
  /** When set, locks the directory to a single workspace: the workspace
   *  dropdown and workspace column are hidden, and every query is
   *  scoped to this id regardless of the global workspace switcher.
   *  Used by the workspace edit page's Users tab. */
  workspaceIdLock?: number | null;
  /** Optional toolbar action (e.g. the "Invite User" CTA on the
   *  standalone page). When provided it renders inline above the bulk
   *  toolbar; when omitted the component falls back to an inline
   *  Invite button so the workspace-tab consumer doesn't need to
   *  duplicate the wiring. */
  toolbarSlot?: React.ReactNode;
  /** Fires whenever the paged total updates. Lets a parent surface
   *  (e.g. the WorkspaceEdit Users tab) render a matching "Users (N)"
   *  header without duplicating the search query. */
  onTotalCountChange?: (total: number) => void;
}

export default function UsersDirectory({ workspaceIdLock, toolbarSlot, onTotalCountChange }: UsersDirectoryProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Use the EFFECTIVE role so an account admin previewing as a
  // workspace admin sees the same chrome a real workspace admin would —
  // notably, the Workspace column collapses away (every row would just
  // repeat the same tenant name) and the audience toggle hides.
  const userRole = useSelector((state: RootState) => state.profile.user?.role);
  // Workspace-scoping affordances (column + dropdown) are only relevant
  // when the viewer can SEE multiple workspaces AND the parent hasn't
  // locked us into one.
  const showWorkspaceAffordances = isAccountOrSystemAdmin(userRole) && !workspaceIdLock;

  // Global switcher locks the workspace scope when set. The page-level
  // filter only takes effect when the global switcher is "All
  // Workspaces" (null). Page filter never writes back to the global
  // switcher. The `workspaceIdLock` prop overrides both — when present
  // every query is forced to that id.
  const switcherWorkspace = useSelector((state: RootState) => state.workspaces.selectedWorkspace);
  const [pageWorkspaceFilter, setPageWorkspaceFilter] = useState<Workspace | null>(null);
  const effectiveWorkspaceId: number | undefined =
    workspaceIdLock ??
    switcherWorkspace?.id ??
    pageWorkspaceFilter?.id ??
    undefined;
  const showPageFilter = showWorkspaceAffordances && !switcherWorkspace;

  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>('all');
  const [roleFilter, setRoleFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  // Audience drives the role query. Learners audience forces role=User;
  // Managers audience forces role to the selected manager sub-type, or
  // defaults to Workspace Admin (most common) when nothing's picked yet.
  // The single-role API constraint means Managers needs a sub-type — the
  // UI surfaces that as a narrowed Role select instead of a forced pick.
  const effectiveRoleFilter: number | null =
    audienceFilter === 'learners'
      ? UserRole.User
      : audienceFilter === 'managers'
        ? roleFilter ?? UserRole.WorkspaceAdmin
        : roleFilter;

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Login | null>(null);
  const [actionType, setActionType] = useState<'delete' | 'disable' | 'enable' | null>(null);

  // Per-row action menu — anchored to the kebab button on each row.
  // Tracking the row alongside the anchor so the menu items have the
  // user context they need without prop-drilling.
  const [rowMenu, setRowMenu] = useState<{ anchorEl: HTMLElement; row: Login } | null>(null);
  const closeRowMenu = () => setRowMenu(null);

  // Assign-content dialog — opens when the "Assign content" row action
  // is picked. Keyed by target user; reset on close so the next open
  // is a fresh form.
  const [assignTarget, setAssignTarget] = useState<Login | null>(null);

  // Bulk selection — set of user IDs ticked in the table.
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkConfirmAction, setBulkConfirmAction] = useState<
    'disable' | 'enable' | 'delete' | null
  >(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  // Column sort — resolved server-side. The column keys used here
  // ('name' | 'email' | 'role' | 'workspace' | 'status' | 'lastLogin')
  // pass straight through to `searchUsersPaged`, which orders the full
  // result set; changing either value refetches from page 1.
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const paged = usePagedList<Login>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        const result = await usersClient.searchUsersPaged(
          effectiveWorkspaceId ?? undefined,
          effectiveRoleFilter ?? undefined,
          statusFilter === 'all' ? undefined : statusFilter,
          search || undefined,
          page,
          pageSize,
          undefined,
          undefined,
          undefined,
          sortBy,
          sortDir
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [effectiveWorkspaceId, effectiveRoleFilter, statusFilter, sortBy, sortDir]
    ),
    deps: [effectiveWorkspaceId ?? null, effectiveRoleFilter, statusFilter, sortBy, sortDir],
    onError: () => alert(enqueueSnackbar, 'Failed to load users', 'error'),
  });

  // Drop bulk selection whenever the underlying query shifts — a stale
  // selection across re-fetches would silently retarget the wrong users.
  useEffect(() => {
    setSelectedIds([]);
  }, [paged.page, paged.pageSize, paged.search, effectiveRoleFilter, statusFilter, audienceFilter, effectiveWorkspaceId]);

  // Forward the live total to any parent that wants to render a header
  // count (e.g. the workspace Users tab's "Users (N)" headline).
  useEffect(() => {
    onTotalCountChange?.(paged.totalCount);
  }, [paged.totalCount, onTotalCountChange]);

  // Workspace deactivation cascades onto its members: a user in a
  // deactivated workspace renders as "Inactive" regardless of their own
  // status. The user search row doesn't carry the workspace's status, so
  // we fetch the workspace list once and build the set of deactivated ids
  // to test each row's `workspaceId` against.
  const [deactivatedWorkspaceIds, setDeactivatedWorkspaceIds] = useState<Set<string>>(
    () => new Set(),
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await workspacesClient.searchWorkspaces(undefined, 1, 1000);
        if (cancelled) return;
        const ids = (result.items ?? [])
          .filter((w) => w.deactivatedAt != null)
          .map((w) => String(w.id));
        setDeactivatedWorkspaceIds(new Set(ids));
      } catch {
        /* non-fatal — fall back to each user's own status */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hydrate the selected ids back into full Login objects for handler
  // calls. Ids not on the current page are silently dropped.
  const selectedUsers: Login[] = paged.items.filter((u) =>
    selectedIds.includes(u.id ?? u.email ?? ''),
  );

  const runBulkAction = async () => {
    if (!bulkConfirmAction || selectedUsers.length === 0) return;
    setBulkRunning(true);
    try {
      // No bulk endpoint today — fan out sequentially. Cheap for tens;
      // a server-side `bulkAction` would be the right move for hundreds.
      let succeeded = 0;
      const errors: string[] = [];
      for (const u of selectedUsers) {
        if (!u.id) continue;
        try {
          if (bulkConfirmAction === 'delete') {
            await usersClient.deleteUser(u.id);
          } else {
            await usersClient.toggleUserStatus(u.id, bulkConfirmAction === 'enable');
          }
          succeeded++;
        } catch {
          errors.push(getUserDisplayName(u));
        }
      }
      const verb =
        bulkConfirmAction === 'delete' ? 'deleted' : bulkConfirmAction === 'enable' ? 'enabled' : 'disabled';
      alert(
        enqueueSnackbar,
        errors.length === 0
          ? `${succeeded} user${succeeded === 1 ? '' : 's'} ${verb}.`
          : `${succeeded} ${verb}; ${errors.length} failed (${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '…' : ''})`,
        errors.length === 0 ? 'success' : 'warning',
      );
      paged.refresh();
      setSelectedIds([]);
    } finally {
      setBulkRunning(false);
      setBulkConfirmAction(null);
    }
  };

  const handleEdit = (student: Login) => {
    navigate(`/students/${student.id}`);
  };

  const handleDelete = (student: Login) => {
    setSelectedStudent(student);
    setActionType('delete');
    setConfirmDialogOpen(true);
  };

  const handleToggleActive = (student: Login) => {
    setSelectedStudent(student);
    setActionType(student.isActive ? 'disable' : 'enable');
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedStudent) return;
    try {
      if (actionType === 'delete') {
        await usersClient.deleteUser(selectedStudent.id!);
        alert(enqueueSnackbar, 'User deleted successfully', 'success');
      } else if (actionType === 'disable' || actionType === 'enable') {
        const newStatus = actionType === 'enable';
        await usersClient.toggleUserStatus(selectedStudent.id!, newStatus);
        alert(
          enqueueSnackbar,
          `User ${actionType === 'disable' ? 'disabled' : 'enabled'} successfully`,
          'success'
        );
      }
      paged.refresh();
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Action failed', 'error');
    } finally {
      setConfirmDialogOpen(false);
      setSelectedStudent(null);
      setActionType(null);
    }
  };

  const handleResetPassword = (student: Login) => {
    setSelectedStudent(student);
    setResetPasswordDialogOpen(true);
  };

  const handleResendInvitation = async (student: Login) => {
    try {
      await usersClient.resendInvitation(student.id);
      alert(enqueueSnackbar, `Invitation email resent to ${student.email}`, 'success');
      paged.refresh();
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to resend invitation', 'error');
    }
  };

  const getConfirmMessage = () => {
    if (!selectedStudent) return '';
    const name = getUserDisplayName(selectedStudent);
    if (actionType === 'delete') return `Are you sure you want to delete ${name}?`;
    if (actionType === 'disable') return `Are you sure you want to disable ${name}'s access?`;
    if (actionType === 'enable') return `Are you sure you want to enable ${name}'s access?`;
    return '';
  };

  const columns: PagedTableColumn<Login>[] = [
    { key: 'name', label: 'Name', sortable: true, render: (s) => getUserDisplayName(s) },
    { key: 'email', label: 'Email', sortable: true, render: (s) => s.email },
    { key: 'role', label: 'Role', sortable: true, render: (s) => getRoleLabel(s.role) },
    // Workspace column suppressed when the directory is locked to a
    // single workspace — every row would just repeat the same value.
    ...(showWorkspaceAffordances
      ? [
          {
            key: 'workspace',
            label: 'Workspace',
            sortable: true,
            render: (s: Login) => (s as any).workspaceName || '—',
          },
        ]
      : []),
    {
      key: 'groups',
      label: 'Groups',
      render: (s) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {s.groups && s.groups.length > 0 ? (
            s.groups.map((group) => (
              <Chip key={group.id} label={group.name} size="small" color="secondary" variant="outlined" />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      // Pastel status pill — softer than the default MUI success/error
      // chips and consistent with the rest of the admin shell's palette
      // (cyan/amber/grey tints used elsewhere).
      //
      // Effective status accounts for the workspace-deactivation
      // cascade: a member of a deactivated workspace shows as
      // "Inactive" regardless of their own `user.isActive`, with a
      // tooltip pointing at the source so admins don't chase the
      // wrong knob.
      render: (s) => {
        const wsId = (s as any).workspaceId;
        const inDeactivatedWs =
          wsId != null && deactivatedWorkspaceIds.has(String(wsId));
        const label = inDeactivatedWs ? 'Inactive' : getUserStatus(s);
        const palette =
          label === 'Active'
            ? { bgcolor: '#e6f7ec', color: '#1e7a3a' }
            : label === 'Pending Invitation'
              ? { bgcolor: 'warning.softBg', color: 'warning.softText' }
              : label === 'Invitation Expired'
                ? { bgcolor: 'error.softBg', color: 'error.softText' }
                : { bgcolor: '#f1f3f5', color: '#5a5f66' };
        const chip = (
          <Chip
            label={label}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 600,
              bgcolor: palette.bgcolor,
              color: palette.color,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        );
        return inDeactivatedWs ? (
          <Tooltip title="This user's workspace is deactivated.">
            <span>{chip}</span>
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      render: (s) => formatDateTime(s.lastLoginAt),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      width: 56,
      render: (s) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
          <Tooltip title="Actions">
            <IconButton
              size="small"
              onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, row: s })}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Inline Invite button used when the parent didn't pass a toolbarSlot —
  // keeps the workspace-tab consumer from having to wire its own
  // dialog state.
  const inlineInviteButton = (
    <Button
      variant="contained"
      size="medium"
      startIcon={<AddIcon />}
      onClick={() => setInviteDialogOpen(true)}
      sx={{ px: 3, alignSelf: 'flex-start' }}
    >
      Invite User
    </Button>
  );

  return (
    <Box>
      {/* Toolbar — either the parent's slotted action (page header
          consumer) OR an inline Invite button (workspace-tab consumer). */}
      {toolbarSlot ?? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {inlineInviteButton}
        </Box>
      )}

      {/* Bulk-action toolbar — slides in when 1+ rows are selected.
          Fixed height so the table doesn't jump. Pastel-tinted to match
          the rest of the admin shell. */}
      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            mb: 1.5,
            borderRadius: 1.5,
            bgcolor: 'rgba(var(--brand-rgb), 0.14)',
            border: '1px solid rgba(var(--brand-rgb), 0.45)',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" fontWeight={700} color="primary.main">
            {selectedIds.length} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<BookmarkIcon />}
            onClick={() => setBulkAssignOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Assign content
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setBulkConfirmAction('enable')}
            sx={{ textTransform: 'none' }}
          >
            Enable
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonOffIcon />}
            onClick={() => setBulkConfirmAction('disable')}
            sx={{ textTransform: 'none' }}
          >
            Disable
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setBulkConfirmAction('delete')}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>
      )}
      <PagedTable<Login>
        columns={columns}
        rows={paged.items}
        totalCount={paged.totalCount}
        page={paged.page}
        pageSize={paged.pageSize}
        loading={paged.loading}
        rowKey={(s) => s.id ?? s.email ?? ''}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(key, dir) => {
          setSortBy(key);
          setSortDir(dir);
        }}
        onPageChange={paged.setPage}
        onPageSizeChange={paged.setPageSize}
        onRowClick={handleEdit}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No users found"
        filtersSlot={
          <>
            {/* Filter row reads left-to-right as a narrowing funnel:
                identify the person (name) → narrow by role → narrow by
                workspace → toggle the partition (Learners vs Managers)
                → flip on inactive rows. Each filter is conditionally
                rendered, so the row still reads cleanly when some
                pieces don't apply (WorkspaceAdmin viewers + the
                workspace-locked tab both shed workspace controls). */}

            {/* Name / email search — anchored at the far left of the
                filters row so it sits directly above the Name column. */}
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={paged.search ?? ''}
              onChange={(e) => paged.setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 280 }}
            />
            {/* Role sub-select. Hidden in Learners mode (the partition
                already pins role=User). In Managers mode the options
                narrow to the three admin roles so the dropdown reads
                as "which kind of manager". */}
            {audienceFilter !== 'learners' && (
              <SelectField
                size="small"
                label={audienceFilter === 'managers' ? 'Manager type' : 'Role'}
                value={roleFilter ?? ''}
                onChange={(e) =>
                  setRoleFilter(e.target.value ? Number(e.target.value) : null)
                }
                options={audienceFilter === 'managers' ? MANAGER_ROLE_OPTIONS : ALL_ROLE_OPTIONS}
                emptyLabel={audienceFilter === 'managers' ? 'All Managers' : 'All Roles'}
                sx={{ width: 180 }}
              />
            )}
            {/* Workspace picker — Super/Account admins only, and only
                when no workspace is pinned in the global switcher AND
                no workspaceIdLock came from the parent. */}
            {showPageFilter && (
              <WorkspaceAutocomplete
                value={pageWorkspaceFilter}
                onChange={setPageWorkspaceFilter}
              />
            )}
            {/* Audience toggle — Super/Account admins only. Page
                partitions into Learners vs Managers as a single mental
                model, while the Role select above acts as a sub-filter
                within the active partition. Visible even when the
                workspace is locked, because the learner/manager split
                is still useful inside a single tenant. */}
            {isAccountOrSystemAdmin(userRole) && (
              <ToggleButtonGroup
                size="small"
                value={audienceFilter}
                exclusive
                onChange={(_e, newValue) => {
                  if (!newValue) return;
                  setAudienceFilter(newValue as AudienceFilter);
                  // Clear the sub-filter on audience change so a
                  // previously-picked admin sub-type doesn't leak into
                  // the Learners view (or vice versa).
                  setRoleFilter(null);
                }}
              >
                <ToggleButton value="all">All Users</ToggleButton>
                <ToggleButton value="learners">Learners</ToggleButton>
                <ToggleButton value="managers">Managers</ToggleButton>
              </ToggleButtonGroup>
            )}
            {/* "Show inactive users" — single toggle replaces the
                three-state All/Active/Inactive strip. Default is off,
                so the page lands on active users only. */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={statusFilter === 'all'}
                  onChange={(e) => setStatusFilter(e.target.checked ? 'all' : 'active')}
                />
              }
              label={
                <Typography variant="body2">Show inactive users</Typography>
              }
              sx={{ ml: 0 }}
            />
          </>
        }
      />

      {/* Row-action menu — single anchor reused across all rows.
          Replaces the inline icon strip so the table column reads as
          one tidy kebab affordance per row. */}
      <Menu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={!!rowMenu}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 1.5 } }}
      >
        {/* Assign content — opens the assign dialog targeting this
            user. Sits at the top of the menu since it's the most
            common reason an admin returns to this page. */}
        <MenuItem
          onClick={() => {
            if (rowMenu) setAssignTarget(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <BookmarkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign content</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) handleEdit(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) handleResetPassword(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MenuItem>
        {rowMenu?.row?.lastLoginAt == null && (
          <MenuItem
            onClick={() => {
              if (rowMenu) handleResendInvitation(rowMenu.row);
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resend Invitation</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (rowMenu) handleToggleActive(rowMenu.row);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            {rowMenu?.row?.isActive ? (
              <PersonOffIcon fontSize="small" />
            ) : (
              <PersonAddIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {rowMenu?.row?.isActive ? 'Disable Access' : 'Enable Access'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) handleDelete(rowMenu.row);
            closeRowMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <InviteStudentDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={paged.refresh}
        lockedWorkspaceId={workspaceIdLock ?? switcherWorkspace?.id}
      />

      {/* Assign content — mounted at component level so the dialog
          persists even after the row menu closes (the user is captured
          in `assignTarget` before the menu dismisses). */}
      <AssignContentDialog
        open={!!assignTarget}
        targetUsers={assignTarget ? [assignTarget] : []}
        onClose={() => setAssignTarget(null)}
      />

      {/* Bulk assign — reuses the same dialog with multiple targets. */}
      <AssignContentDialog
        open={bulkAssignOpen}
        targetUsers={selectedUsers}
        onClose={() => setBulkAssignOpen(false)}
        onAssigned={() => {
          setBulkAssignOpen(false);
          setSelectedIds([]);
        }}
      />

      {/* Bulk enable/disable/delete confirmation — fan-out runs
          sequentially against the per-user API since the backend
          doesn't expose a bulk endpoint today. */}
      <ConfirmDialog
        open={!!bulkConfirmAction}
        title={
          bulkConfirmAction === 'delete'
            ? `Delete ${selectedUsers.length} user${selectedUsers.length === 1 ? '' : 's'}?`
            : bulkConfirmAction === 'disable'
              ? `Disable access for ${selectedUsers.length}?`
              : bulkConfirmAction === 'enable'
                ? `Enable access for ${selectedUsers.length}?`
                : ''
        }
        content={
          bulkConfirmAction === 'delete'
            ? 'This permanently removes the selected users. Their enrollments and progress data go with them. This action cannot be undone.'
            : bulkConfirmAction === 'disable'
              ? 'Selected users will lose access to the LMS until re-enabled.'
              : 'Selected users will regain access to the LMS.'
        }
        onConfirm={runBulkAction}
        onCancel={() => !bulkRunning && setBulkConfirmAction(null)}
      />

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        user={selectedStudent}
        onClose={() => {
          setResetPasswordDialogOpen(false);
          setSelectedStudent(null);
        }}
        onSuccess={paged.refresh}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        title={
          actionType === 'delete'
            ? 'Delete User'
            : actionType === 'disable'
              ? 'Disable Access'
              : 'Enable Access'
        }
        content={getConfirmMessage()}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setSelectedStudent(null);
          setActionType(null);
        }}
      />
    </Box>
  );
}

